import { useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { ArrowLeftRightIcon, PlusIcon } from 'lucide-react'

import { CURRENCY_CODES } from '@ems/money/currency'
import { Button } from '@ems/ui/components/button'
import { formatDateTime } from '@ems/ui/lib/format'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'

import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import { PaymentMethodsSkeleton } from '#admin/features/billing/billing-skeletons.tsx'
import { ExchangeRateDialog } from '#admin/features/billing/exchange-rate-dialog.tsx'
import { rateText, type ExchangeRate } from '#admin/features/billing/exchange-rates-api.ts'
import { useRemoveExchangeRate } from '#admin/features/billing/mutations.ts'
import { exchangeRatesQuery } from '#admin/features/billing/queries.ts'

/**
 * `/billing/exchange-rates` — the exchange rates of this edition. A new draft invoice in a
 * different currency gets a copy of the rate. Each edition has its own rates.
 */
export const Route = createFileRoute('/_authenticated/billing/exchange-rates')({
  // ponytail: the payment methods skeleton has the same shape (a header and one short table).
  pendingComponent: PaymentMethodsSkeleton,
  staticData: { crumb: { parents: [{ label: 'Billing' }], label: 'Exchange rates' } },
  loader: ({ context }) => context.queryClient.ensureQueryData(exchangeRatesQuery()),
  component: ExchangeRatesPage,
})

type Dialog = { kind: 'edit'; rate: ExchangeRate | null } | { kind: 'remove'; rate: ExchangeRate }

function ExchangeRatesPage() {
  const { data } = useSuspenseQuery(exchangeRatesQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const canManage = can(session, 'billing.manage')
  const remove = useRemoveExchangeRate()
  const [dialog, setDialog] = useState<Dialog | null>(null)

  const taken = data.items.map((rate) => rate.currency)
  // Every currency except the one of the edition can have a rate.
  const canAdd = CURRENCY_CODES.some(
    (code) => code !== data.paymentCurrency && !taken.includes(code),
  )

  const columns: ColumnDef<ExchangeRate, never>[] = [
    {
      accessorKey: 'currency',
      header: 'Currency',
      meta: { primary: true },
    },
    {
      id: 'rate',
      header: 'Rate',
      cell: ({ row }) =>
        rateText(row.original.currency, row.original.rate, row.original.paymentCurrency),
    },
    {
      id: 'updated',
      header: 'Changed',
      meta: { hideOnCard: true },
      cell: ({ row }) => (
        <span className="text-muted-foreground">{formatDateTime(row.original.updatedAt)}</span>
      ),
    },
    ...(canManage
      ? [
          {
            id: 'actions',
            header: '',
            meta: { align: 'end', shrink: true },
            cell: ({ row }) => (
              <span className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDialog({ kind: 'edit', rate: row.original })}
                >
                  Change
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDialog({ kind: 'remove', rate: row.original })}
                >
                  Remove
                </Button>
              </span>
            ),
          } satisfies ColumnDef<ExchangeRate, never>,
        ]
      : []),
  ]

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Exchange rates"
        description={`Set the rate for invoices in a currency that is not ${data.paymentCurrency}. A new invoice gets the rate. Invoices that exist keep their rate.`}
        actions={
          canManage && canAdd ? (
            <Button onClick={() => setDialog({ kind: 'edit', rate: null })}>
              <PlusIcon />
              Add rate
            </Button>
          ) : null
        }
      />

      {canManage ? null : <ReadOnlyNotice area="exchange rates" />}

      <DataTable
        caption="Exchange rates"
        title={`${data.items.length} ${data.items.length === 1 ? 'rate' : 'rates'}`}
        columns={columns}
        data={data.items}
        getRowId={(rate) => rate.currency}
        empty={
          <EmptyState
            icon={<ArrowLeftRightIcon />}
            title="No exchange rates"
            description={`An invoice in a currency that is not ${data.paymentCurrency} gets no rate. A payment in ${data.paymentCurrency} cannot settle it.`}
          />
        }
      />

      {dialog?.kind === 'edit' ? (
        <ExchangeRateDialog
          rate={dialog.rate}
          paymentCurrency={data.paymentCurrency}
          taken={taken}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog?.kind === 'remove' ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => (open ? undefined : setDialog(null))}
          title={`Remove the ${dialog.rate.currency} rate?`}
          description={`New invoices in ${dialog.rate.currency} get no rate. Invoices that exist keep their rate.`}
          confirmLabel={remove.isPending ? 'Removing…' : 'Remove rate'}
          destructive
          busy={remove.isPending}
          onConfirm={() =>
            remove.mutate(dialog.rate.currency, { onSettled: () => setDialog(null) })
          }
        />
      ) : null}
    </div>
  )
}
